import { AnyDelta, AnyEntity } from "@iterate/common/types/domain";
import { AnyEvent } from "@iterate/common/types/events";
import { db } from "./dexie";
import { Table } from "dexie";
import { z } from "zod/v4";
import { syncEntityByType, type SyncEntityKind } from "./recordSchemas";

type ModelMap<K extends SyncEntityKind> = {
  table: () => Table<z.output<(typeof syncEntityByType)[K]["recordSchema"]>>;
  toolkit: (typeof syncEntityByType)[K];
};

function getTools<K extends SyncEntityKind>(type: K): ModelMap<K> {
  switch (type) {
    case "metric_series":
      return {
        table: () => db.metricSeries,
        toolkit: syncEntityByType.metric_series,
      } as ModelMap<K>;
    case "goal":
      return {
        table: () => db.goals,
        toolkit: syncEntityByType.goal,
      } as ModelMap<K>;
    case "habit":
      return {
        table: () => db.habits,
        toolkit: syncEntityByType.habit,
      } as ModelMap<K>;
    case "instance":
      return {
        table: () => db.instances,
        toolkit: syncEntityByType.instance,
      } as ModelMap<K>;
    case "journal":
      return {
        table: () => db.journals,
        toolkit: syncEntityByType.journal,
      } as ModelMap<K>;
    case "metric":
      return {
        table: () => db.metrics,
        toolkit: syncEntityByType.metric,
      } as ModelMap<K>;
    case "task":
      return {
        table: () => db.tasks,
        toolkit: syncEntityByType.task,
      } as ModelMap<K>;
    default:
      throw new Error(`Unknown type: ${type}`);
  }
}

async function update(rawDelta: AnyDelta): Promise<void> {
  const tools = getTools(rawDelta.type);
  const table = tools.table();
  const toolkit = tools.toolkit;
  const record = await table.get(rawDelta.id);
  if (!record) {
    throw new Error(`Record not found for delta: ${rawDelta.id}`);
  }
  const entity = toolkit.recordSchema.encode(record as never) as z.infer<
    (typeof toolkit)["entitySchema"]
  >;
  const delta = toolkit.deltaSchema.parse(rawDelta) as z.infer<(typeof toolkit)["deltaSchema"]>;
  const merged = { ...entity, ...delta } as z.infer<(typeof toolkit)["entitySchema"]>;
  const mergedRecord = toolkit.recordSchema.decode(merged as never) as never;
  console.log("MERGED RECORD", mergedRecord);
  await table.put(mergedRecord);
}

async function del(entity: Pick<AnyEntity, "id" | "type">): Promise<void> {
  const tools = getTools(entity.type);
  const table = tools.table();
  await table.delete(entity.id);
}

async function merge(event: AnyEvent) {
  switch (event.type) {
    case "update":
      return await update(event.delta);
    case "delete":
      return await del(event.entity);
    case "create": {
      const tools = getTools(event.entity.type);
      const createRecord = tools.toolkit.recordSchema.decode(event.entity as never);
      console.log("CREATE", createRecord);
      await tools.table().put(createRecord);
    }
  }
}

async function addEvent(event: AnyEvent) {
  switch (event.type) {
    case "update": {
      const deltaRecord = event.delta;
      await db.eventLog.put({ ...event, delta: deltaRecord });
      break;
    }
    case "create": {
      const createRecord = event.entity;
      await db.eventLog.put({ ...event, entity: createRecord });
      break;
    }
  }
}

export const LocalDb = {
  merge,
  addEvent,
};
